import React, { useState, useEffect, useRef } from 'react';
import './Testimonials.css';
import { FaStar, FaGoogle, FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Testimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Témoignages
  const testimonials = [
    {
      id: 1,
      name: 'Sophie Martin',
      date: 'Il y a 2 mois',
      rating: 5,
      text: 'Excellent professionnel ! Intervention rapide pour une fuite urgente. Travail soigné, propre et tarif honnête. Je recommande vivement pour la qualité du service.',
      avatar: 'SM',
      service: 'Dépannage urgence'
    },
    {
      id: 2,
      name: 'Pierre Dubois',
      date: 'Il y a 1 mois',
      rating: 5,
      text: 'Très satisfait de la rénovation complète de ma salle de bain. Professionnel, ponctuel et de bon conseil. Le résultat est au-delà de mes attentes !',
      avatar: 'PD',
      service: 'Rénovation salle de bain'
    },
    {
      id: 3,
      name: 'Marie Lefebvre',
      date: 'Il y a 3 semaines',
      rating: 5,
      text: 'Installation d\'un chauffe-eau thermodynamique impeccable. Prise le temps de tout m\'expliquer sur le fonctionnement et l\'entretien. Un vrai expert !',
      avatar: 'ML',
      service: 'Installation chauffe-eau'
    },
    {
      id: 4,
      name: 'Jean-Claude Rousseau',
      date: 'Il y a 2 semaines',
      rating: 5,
      text: 'Intervention rapide pour un WC bouché. Problème résolu en 1h. Très professionnel et sympathique. Je le recommande sans hésitation pour son efficacité.',
      avatar: 'JR',
      service: 'Débouchage'
    },
    {
      id: 5,
      name: 'Fatima Benali',
      date: 'Il y a 1 semaine',
      rating: 5,
      text: '20 ans d\'expérience, ça se voit ! Détecté et réparé une fuite invisible grâce à sa caméra thermique. Travail de qualité avec garantie. Excellent rapport qualité/prix.',
      avatar: 'FB',
      service: 'Détection de fuite'
    },
    {
      id: 6,
      name: 'Thomas Bernard',
      date: 'Il y a 5 jours',
      rating: 5,
      text: 'Installation de robinetterie design dans ma cuisine. Travail soigné et propre. Très bon conseiller et à l\'écoute de mes besoins. Je referai appel à lui !',
      avatar: 'TB',
      service: 'Installation sanitaire'
    }
  ];

  // Auto-slide
  useEffect(() => {
    if (!isPaused && isVisible) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % testimonials.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isPaused, isVisible, testimonials.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <FaStar 
        key={index} 
        className={index < rating ? 'star-filled' : 'star-empty'} 
      />
    ));
  };

  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentSlide + i + testimonials.length) % testimonials.length;
      visible.push({ ...testimonials[index], position: i });
    }
    return visible;
  };

  return (
    <section id="avis" className="section testimonials" ref={sectionRef}>
      <div className="testimonials-bg">
        <div className="testimonials-shape shape-1"></div>
        <div className="testimonials-shape shape-2"></div>
      </div>

      <div className="container">
        <div className="section-title">
          <h2>Avis Clients</h2>
          <p>Ce que nos clients disent de notre travail et de notre service</p>
        </div>

        {/* Google Rating Summary */}
        <div className={`google-rating-card ${isVisible ? 'reveal active' : 'reveal'}`}>
          <div className="google-logo">
            <FaGoogle />
          </div>
          <div className="rating-content">
            <div className="rating-stars-large">
              {renderStars(5)}
            </div>
            <div className="rating-score">
              <span className="score">5.0</span>
              <span className="separator">/</span>
              <span className="max">5</span>
            </div>
            <p className="rating-text">Basé sur {testimonials.length} avis Google</p>
          </div>
          <a 
            href="https://www.google.com/maps" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-outline"
          >
            <FaGoogle /> Voir tous les avis
          </a>
        </div>

        {/* Carousel */}
        <div 
          className={`testimonials-carousel ${isVisible ? 'reveal active' : 'reveal'}`}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <button 
            className="carousel-nav carousel-prev" 
            onClick={goToPrevious}
            aria-label="Avis précédent"
          >
            <FaChevronLeft />
          </button>

          <div className="carousel-track">
            {getVisibleTestimonials().map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`testimonial-card ${
                  testimonial.position === 0 ? 'active' : 
                  testimonial.position === -1 ? 'prev' : 'next'
                }`}
              >
                <div className="testimonial-header">
                  <div className="testimonial-avatar">
                    {testimonial.avatar}
                    <div className="avatar-ring"></div>
                  </div>
                  <div className="testimonial-info">
                    <h4>{testimonial.name}</h4>
                    <div className="testimonial-rating">
                      {renderStars(testimonial.rating)}
                    </div>
                    <span className="testimonial-date">{testimonial.date}</span>
                  </div>
                  <FaGoogle className="google-badge" />
                </div>

                <div className="testimonial-service-tag">
                  {testimonial.service}
                </div>

                <div className="testimonial-body">
                  <FaQuoteLeft className="quote-icon" />
                  <p>{testimonial.text}</p>
                </div>

                <div className="testimonial-verified">
                  <FaStar className="verified-icon" />
                  <span>Avis vérifié Google</span>
                </div>
              </div>
            ))}
          </div>

          <button 
            className="carousel-nav carousel-next" 
            onClick={goToNext}
            aria-label="Avis suivant"
          >
            <FaChevronRight />
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="carousel-dots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`dot ${currentSlide === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Aller à l'avis ${index + 1}`}
            />
          ))}
        </div>

        {/* Stats */}
        <div className={`testimonials-stats ${isVisible ? 'reveal active' : 'reveal'}`}>
          <div className="stat-item">
            <div className="stat-value">100%</div>
            <div className="stat-label">Clients Satisfaits</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-value">5/5</div>
            <div className="stat-label">Note Moyenne</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-value">500+</div>
            <div className="stat-label">Avis Positifs</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
